/**
 * User Activity as a standalone screen — `/user-activity`.
 *
 * The SAME view the Users screen renders in its Activity tab, given its own
 * page for the people who can see the activity but not the Users screen.
 *
 * Why two homes, and why that isn't a duplicate:
 *   - Seeing Users is an email allowlist (`canAccessUsersTab`); seeing activity
 *     is membership of the "User Activity" org group. Those are different
 *     populations and neither contains the other.
 *   - A group member who is NOT on the Users allowlist can't be sent to
 *     `/users/activity`: the tab bar beside it is All Users / Active Sessions /
 *     Groups, every one of which 403s for them. Landing them on a screen where
 *     three of the four tabs are broken is worse than giving activity its own
 *     page.
 *   - So: on the allowlist → it stays a Users tab and this route is not
 *     advertised. Off the allowlist but in the group → this page, reached from
 *     its own header entry. Exactly one entry point per person, never both.
 *
 * The nav decides which; see AuthenticatedDashboard. This screen re-checks
 * membership itself, because a route is reachable by typing it.
 *
 * NOTE: it lives in the UsersScreen/UserActivity folder rather than a top-level
 * screen folder deliberately — it is a thin wrapper around that view, and
 * splitting the two apart would just make the pair easy to miss.
 */
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import { Navigate } from "react-router-dom";

import { useIsInUserActivityGroup } from "../../../hooks/useIsInUserActivityGroup";
import { T } from "../../SupportScreen/components/supportUi";
import UserActivityView from "./UserActivityView";

export default function UserActivityScreen() {
  const { allowed, loading } = useIsInUserActivityGroup();

  // Nothing until the group lookup settles: rendering the view first would fire
  // its admin-gated fetch on someone who may not be allowed to see any of it.
  if (loading) return null;
  if (!allowed) return <Navigate to="/loans" replace />;

  return (
    <Box
      sx={{
        background: T.bg,
        minHeight: "100%",
        height: "100%",
        overflowY: "auto",
        fontFamily: "inherit",
      }}
    >
      {/* Same frame as the Users screen, so the view sits identically whichever
          door you came through. */}
      <Container
        maxWidth={false}
        sx={{ maxWidth: "1800px", pt: "30px", pb: "64px" }}
      >
        <Box
          sx={{
            fontSize: 23,
            fontWeight: 700,
            letterSpacing: "-.01em",
            color: T.ink900,
          }}
        >
          User Activity
        </Box>
        <Box sx={{ color: T.ink500, mt: "5px", fontSize: 13.5 }}>
          What each person has been doing across the portal.
        </Box>

        <UserActivityView />
      </Container>
    </Box>
  );
}
